import React from 'react';
import { Link, graphql } from 'gatsby';
import useStoryblok from '~/utils/storyblok-service';

import Layout from '~/components/Layout';
import Seo from '~/components/SEO';
import TextCard from '~/components/Dynamic/TextCard';
import TwoColumns from '~/components/TwoColumns';
import IconLink from '~/components/IconLink';
import QuoteText from '~/components/QuoteText';
// import JobListing from '~/components/JobListing';
import CompanyBottomNav from '~/components/CompanyBottomNav';
// import DynamicComponent from '~/components/DynamicComponent';
import Image from '~/components/Dynamic/Image';

// import chevronUp from '~/icons/chevron-up.svg';
// import ChevUp from '~/icons/chevron-up-bg-blue.svg';
// import ChevDown from '~/icons/chevron-down-bg-tran.svg';
import sparksBlue from '~/icons/sparks-blue.svg';
import JobCategory from '~/components/JobCategory';

const CareersPage = ({ data, location, pageContext }) => {
  const story = useStoryblok(data.storyblokEntry, location);
  const content = story.content;

  const {
    aboutUs,
    aboutUsImages,
    beliefs,
    buttons,
    cantFindButton,
    cantFindHeading,
    careers,
    hero,
    intro,
    introImages,
    jobs,
    lifeAtBoom,
    lifeAtBoomImage,
    stories,
    storiesHeading,
    videoPreviewImage,
    videoLink,
  } = content;

  return (
    <Layout locale={pageContext.locale}>
      <Seo {...content.seo} lang={story.lang} pageContext={pageContext} />

      {/* Hero */}
      <section className="my-16 mb-20 md:mt-36 md:my-28">
        <div className="container">
          <TextCard
            blok={{
              ...hero[0]?.textCard[0],
            }}
          />
        </div>
      </section>

      <section className="my-20 md:my-28">
        <div className="container">
          <div className="flex flex-wrap -ml-6">
            <div className="flex flex-col justify-center w-full pl-6 md:w-6/12">
              <div className="mb-16 md:mb-24 md:max-w-md">
                <div className="">
                  <Image blok={intro[0]?.icon} />
                </div>
                <h2 className="mt-8 text-h2">{intro[0]?.heading}</h2>
                <div className="mt-3 text-p1 text-20px">
                  <p>{intro[0]?.content}</p>
                </div>
              </div>
              <div className="mb-6 md:mb-0">
                <Image blok={introImages[0]} />
              </div>
            </div>
            <div className="w-full pl-6 md:w-6/12">
              <Image blok={introImages[1]} className="mb-4" />
              <Image blok={introImages[2]} />
            </div>
          </div>
        </div>
      </section>

      <section className="relative my-20 bg-black md:my-28">
        <div className="container py-20 md:py-24">
          <div className="flex flex-wrap items-center -ml-28">
            <div className="w-full pl-28 md:w-6/12 lg:w-5/12">
              <div className="text-white md:max-w-md">
                <h2 className="text-h2">{lifeAtBoom[0]?.heading}</h2>
                <div className="mt-3 text-p2">
                  <p>{lifeAtBoom[0]?.content}</p>
                </div>
              </div>
            </div>
            <div className="flex-auto w-full mt-12 md:w-6/12 lg:w-7/12 pl-28 md:mt-0 ">
              <div className="">
                <Image blok={lifeAtBoomImage} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="my-20 md:my-28">
        <div className="container">
          <div className="flex flex-col items-center justify-center max-w-3xl mx-auto text-center">
            <h2 className="mb-5 text-h2">{aboutUs[0]?.heading}</h2>
            <div className="max-w-2xl mb-5 text-17px">
              <p>{aboutUs[0]?.content}</p>
            </div>
            <div>
              <IconLink to={aboutUs[0]?.button[0]?.button?.url} className={`font-extrabold text-16px`} type="link">
                {aboutUs[0]?.button[0]?.title}
              </IconLink>
            </div>
          </div>
          <div className="mt-20">
            <Image blok={aboutUsImages[0]} />
          </div>
        </div>
      </section>

      <section className="my-20 bg-beige md:my-28 lg:my-32">
        <div className="container py-20 md:py-28">
          <TwoColumns
            text={
              <TextCard className="max-w-lg" blok={beliefs[0].content[0]} contentClass="text-20px leading-relaxed" />
            }
            image={
              <div className="w-full max-w-sm mx-auto">
                <Image blok={beliefs[0]?.content[1]?.image} />
              </div>
            }
          />
        </div>
      </section>

      <section className="my-20 md:my-28 lg:my-32">
        <div className="container">
          <div className="block mb-12 text-center md:mb-16 md:text-left">
            <h2 className="text-h2">{storiesHeading}</h2>
          </div>
          <div className="flex flex-wrap -ml-24 ">
            <div className="w-full pl-24 md:w-6/12 ">
              <div className="mb-11">
                <a
                  href={videoLink || 'https://www.youtube.com/channel/UC3LCz511UOJoVRIdEx4rO-Q'}
                  target="_blank"
                  className="relative flex flex-col rounded-sm shadow-xl group"
                >
                  <Image blok={videoPreviewImage} />
                  <div className="absolute top-0 bottom-0 left-0 right-0 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="flex items-center justify-center w-12 h-12 transition-transform duration-300 transform rounded-full bg-blue group-hover:scale-125">
                      <div
                        className="ml-1 border"
                        style={{
                          borderColor: 'transparent transparent transparent white',
                          borderWidth: '.5rem 0 .5rem 0.75rem',
                        }}
                      ></div>
                    </div>
                  </div>
                </a>
              </div>
              <QuoteText
                className={`mb-8 md:mb-9 `}
                content={stories[0].quote}
                name={stories[0].cite}
                title={stories[0].companyName}
              />
              <div className="mt-6 border-b border-black md:hidden md:border-0 border-opacity-70"></div>
            </div>
            <div className="w-full pl-24 mt-6 md:w-6/12 md:mt-0">
              <div>
                {stories.slice(1, 3).map((story, i) => (
                  <React.Fragment key={`careerStory${i}`}>
                    <div className={`${i === 0 ? 'mb-8 md:mb-14' : 'mt-8 md:mt-14'}`}>
                      <QuoteText
                        className={`mb-6 md:mb-9`}
                        content={story?.quote}
                        name={story?.cite}
                        title={story?.companyName}
                      />
                    </div>
                    {i === 0 && <div className="max-w-md border-b border-black border-opacity-70"></div>}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="positions" className="mt-20 md:mt-28 lg:mt-32">
        <div className="container">
          <div className="flex-wrap items-center justify-between md:flex">
            <div className="items-center md:flex">
              <Image blok={careers[0]?.icon} className="max-w-[65px] md:max-w-[113px]" />
              <div className="text-center md:text-left md:ml-8">
                <h2 className="mb-3 text-h2">{careers[0]?.heading}</h2>
                <div className="text-20px">
                  <p>{careers[0]?.content}</p>
                </div>
              </div>
            </div>
            <div className="hidden mt-8 font-semibold text-center md:mt-12 text-18px font-heading lg:mt-0">
              <label>
                <select name="industry" className="w-full h-12 pl-4 font-bold border pr-28 text-16px font-heading">
                  <option value="#" disabled selected>

                    All locations
                  </option>
                </select>
              </label>
            </div>
          </div>

          <div className="mt-12 md:mt-28">
            {jobs.map((jobCat, i) => (
              <JobCategory key={`jobCat${i}`} {...jobCat} />
            ))}
          </div>
        </div>
      </section>

      <section className="mb-20 md:mb-28">
        <div className="container">
          <div className="border-t border-opacity-50 border-grey"></div>
          <div className="flex-wrap items-center justify-center py-8 -ml-6 text-center md:flex md:py-12 md:text-left">
            <div className="inline-block w-16 pl-6">
              <img src={sparksBlue} alt="sparks blue" className="object-contain" />
            </div>
            <h4 className="pl-6 font-bold md:pl-3 text-18px">{cantFindHeading}</h4>
            <div className="pl-6 mt-6 md:mt-0">
              <a
                href={cantFindButton[0]?.button?.url}
                className={` c-btn c-btn--outline c-btn--shorter
                c-btn--outline--black text-center justify-center
              `}
              >
                {cantFindButton[0]?.title}
              </a>
            </div>
          </div>

          <div className="border-t border-opacity-50 border-grey"></div>
        </div>
      </section>

      <CompanyBottomNav items={buttons} />
    </Layout>
  );
};

export default CareersPage;

export const query = graphql`
  query CareersQuery($id: String!) {
    storyblokEntry(id: { eq: $id }) {
      id
      name
      content
      lang
      slug
      uuid
      full_slug
    }
  }
`;
